import { GridColDef } from "@mui/x-data-grid-pro";
import { Button, IconButton, Tooltip } from "@mui/material";

import ItemStatus from "../../../../../../common/components/item_status";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import dayjs from "dayjs";

/**
 * GenerateTreinamentosColumnsDefinition
 */

type Props = {
  onAvaliarTreinamento: (idTreinamento: number) => void,
  onExportar: (idTreinamento: number) => void,
  onEditar: (idTreinamento: number) => void,
  onDeletar: (idTreinamento: number) => void,
};

export const GenerateTreinamentosColumnsDefinition = ({
  onAvaliarTreinamento,
  onExportar,
  onEditar,
  onDeletar,
}: Props): GridColDef[] => {
  const computeItemStatusTitle = (status: number) => {
    switch (status) {
      case 1: return "A";
      case 2: return "D";
      case 3: return "N";
      case 4: return "A";
      default:
        return "?";
    }
  };

  const computeItemStatusColor = (status: number) => {
    switch (status) {
      case 1: return "red";
      case 2: return "blue";
      case 3: return "yellow";
      case 4: return "green";
      default:
        return "blue";
    }
  };

  const computeItemStatusDescription = (status: number) => {
    switch (status) {
      case 1: return "Avaliação em Atraso";
      case 2: return "Dentro do Prazo";
      case 3: return "Não Realizado";
      case 4: return "Avaliados";
      default:
        return "?";
    }
  };

  const computeAvaliarTreinamentoClass = (avaliarTreinamento?: boolean): string => {
    if (avaliarTreinamento === true) {
      return '#35aa47';
    } else if (avaliarTreinamento === false) {
      return '#d84a38';
    } else {
      return "#ffb848";
    }
  }

  const computeEficazClass = (eficaz?: boolean): string => {
    switch (eficaz) {
      case true: return 'bg-[#45b6af] text-white py-[.1rem] px-[.4rem] font-normal';
      case false: return 'bg-[#f3565d] text-white py-[.1rem] px-[.4rem] font-normal';
      default: return 'bg-[#a6a6a6] text-white text-sm py-[.1rem] px-[.4rem] font-normal';
    }
  }

  const computeEficazLabel = (eficaz?: boolean): string => {
    switch (eficaz) {
      case true: return 'Sim';
      case false: return 'Não';
      default: return 'Não avaliado';
    }
  }

  const computeAssinadoClass = (assinado: boolean): string => {
    return assinado
      ? 'bg-[#45b6af] text-white py-[.1rem] px-[.4rem] font-normal'
      : 'bg-[#f3565d] text-white py-[.1rem] px-[.4rem] font-normal';
  }

  return [
    {
      field: "status",
      filterable: false,
      headerName: "Status",
      width: 65,
      sortable: false,
      align: 'center',
      renderCell: (params) => (
        <div className="w-full h-full flex justify-center items-center">
          <ItemStatus
            title={computeItemStatusTitle(params.value)}
            color={computeItemStatusColor(params.value)}
            description={computeItemStatusDescription(params.value)} />
        </div>
      )
    },
    {
      field: "avaliarTreinamento",
      filterable: false,
      headerName: "",
      width: 40,
      sortable: false,
      align: 'center',
      headerClassName: 'funcionarios-header',
      valueGetter: (value, row) => { return ({ value, id: row['idFuncionarioTreinamento'] as number, }) },
      renderCell: ({ value: { value, id } }) => (
        <div className="w-full h-full flex justify-center items-center">
          <Tooltip title="Avaliar FuncionarioTreinamento">
            <Button
              onMouseDown={() => onAvaliarTreinamento(id)}
              sx={{ background: computeAvaliarTreinamentoClass(value), minWidth: '25px', maxWidth: '25px' }}>
              <DynamicIcon nameIcon="CheckBox" size={15} className="text-white" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
    {
      field: "treinamento",
      filterable: false,
      headerName: "Treinamento",
      width: 130,
      sortable: false,
      align: 'left',
    },
    {
      field: "versao",
      filterable: false,
      headerName: "Versão",
      width: 70,
      sortable: false,
      headerClassName: 'funcionarios-header',
      renderCell: (params) => (
        <div>{params.value ?? "-"}</div>
      ),
    },
    {
      field: "instrutor",
      filterable: false,
      headerName: "Instrutor",
      flex: 1,
      minWidth: 100,
      sortable: false,
      headerClassName: 'funcionarios-header',
    },
    {
      field: "dataInicio",
      filterable: false,
      headerName: "Data",
      flex: 0,
      minWidth: 85,
      sortable: false,
      headerClassName: 'funcionarios-header',
      renderCell: (params) => (
        <div>{params.value && dayjs(params.value).format("DD/MM/YYYY")}</div>
      ),
    },
    {
      field: "prazo",
      filterable: false,
      headerName: "Prazo",
      flex: 0,
      width: 60,
      sortable: false,
      headerClassName: 'funcionarios-header',
    },
    {
      field: "dataPrevAvaliacao",
      filterable: false,
      headerName: "Data Prev. Avaliação",
      sortable: false,
      headerClassName: 'funcionarios-header',
      renderCell: (params) => (
        <div>{params.value && dayjs(params.value).format("DD/MM/YYYY")}</div>
      ),
    },
    {
      field: "dataAvaliacao",
      filterable: false,
      headerName: "Data Avaliação",
      sortable: false,
      headerClassName: 'funcionarios-header',
      renderCell: (params) => (
        <div>{params.value && dayjs(params.value).format("DD/MM/YYYY")}</div>
      ),
    },
    {
      field: "avaliador",
      filterable: false,
      headerName: "Avaliador",
      flex: 1,
      minWidth: 100,
      sortable: false,
    },
    {
      field: "eficaz",
      filterable: false,
      headerName: "Eficaz?",
      width: 120,
      sortable: false,
      renderCell: (params) => {
        return (
          <div>
            <span className={computeEficazClass(params.value)}>
              {computeEficazLabel(params.value)}
            </span>
          </div>
        );
      },
    },
    {
      field: "assinado",
      filterable: false,
      headerName: "Assinado",
      width: 85,
      sortable: false,
      renderCell: (params) => {
        return (
          <div>
            <span className={computeAssinadoClass(params.value)}>
              {params.value === true ? "Sim" : "Não"}
            </span>
          </div>
        );
      },
    },
    {
      field: "exportar",
      filterable: false,
      headerName: "Exportar",
      sortable: false,
      valueGetter: (_, row) => { return row['idFuncionarioTreinamento'] as number; },
      renderCell: ({ value }) => {
        return (
          <Button
            onMouseDown={() => onExportar(value)}
            sx={{
              fontSize: 11,
              paddingX: '.4rem',
              paddingY: 0,
              background: '#e6e6e6',
              borderLeft: '.2rem solid #45b6af',
              borderRadius: 0,
              color: 'black',
              fontWeight: 'normal',
            }}>
            Exportar
          </Button>
        );
      },
    },
    {
      field: "acoes",
      filterable: false,
      headerName: "Ações",
      sortable: false,
      valueGetter: (_, row) => row['idFuncionarioTreinamento'] as number,
      renderCell: ({ value }) => {
        return (
          <div className="flex items-center justify-center w-full h-full gap-2">
            <IconButton onClick={() => onEditar(value)}>
              <DynamicIcon nameIcon="EditRounded" size={25} className="dark:text-white text-primary-green" />
            </IconButton>

            <IconButton onClick={() => onDeletar(value)}>
              <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
            </IconButton>
          </div>
        );
      }
    },
  ];
}